import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ExternalLink } from 'lucide-react';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@shared/ui/sidebar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@shared/ui/collapsible';
import { Badge } from '@shared/ui/badge';
import { SuiteIcon } from '@shared/icons';
import { SuiteNavItem } from '@suites/manifest';
import { useAuth } from '@shared/auth/AuthContext';
import { cn } from '@shared/lib/utils';

export interface SidebarNavGroupProps {
  groupName?: string;
  items: SuiteNavItem[];
  isOpen?: boolean;
  onToggle?: () => void;
}

export const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  groupName,
  items,
  isOpen = true,
  onToggle,
}) => {
  const { hasPermission } = useAuth();
  const { pathname } = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isSidebarCollapsed = state === 'collapsed';

  const visibleItems = items.filter((item) =>
    item.permission ? hasPermission(item.permission) : true
  );

  if (visibleItems.length === 0) return null;

  const effectiveOpen = isSidebarCollapsed ? true : isOpen;

  const menu = (
    <SidebarMenu>
      {visibleItems.map((item) => {
        const isActive = pathname === item.path || pathname.startsWith(`${item.path}/`);

        const isNewTabStudio = item.path === '/webbuilder/pages';

        return (
          <SidebarMenuItem key={item.path}>
            <SidebarMenuButton
              asChild
              isActive={isActive}
              tooltip={item.label}
              className="gap-2.5 px-3 font-medium text-xs text-sidebar-foreground touch:min-h-11"
            >
              {isNewTabStudio ? (
                <a
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 w-full"
                >
                  <SuiteIcon name={item.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate flex-1">{item.label}</span>
                  <ExternalLink className="h-3 w-3 ml-auto opacity-50 text-muted-foreground" />
                </a>
              ) : (
                <NavLink
                  to={item.path}
                  onClick={() => {
                    if (isMobile) setOpenMobile(false);
                  }}
                >
                  <SuiteIcon name={item.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate flex-1">{item.label}</span>
                  {item.status === 'planned' && (
                    <Badge
                      variant="secondary"
                      className="ml-auto text-[9px] px-1 py-0 font-normal uppercase tracking-wider text-muted-foreground"
                    >
                      Soon
                    </Badge>
                  )}
                </NavLink>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  if (!groupName) {
    return <SidebarGroup className="py-1">{menu}</SidebarGroup>;
  }

  return (
    <SidebarGroup className="py-0.5">
      <Collapsible open={effectiveOpen} onOpenChange={onToggle}>
        <SidebarGroupLabel asChild className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-2">
          <CollapsibleTrigger
            type="button"
            className="flex w-full items-center justify-between py-1 px-1.5 rounded-md hover:bg-sidebar-accent/50 cursor-pointer transition-colors group select-none"
          >
            <span className="truncate">{groupName}</span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 shrink-0 transition-transform duration-200 text-muted-foreground group-hover:text-sidebar-foreground',
                effectiveOpen && 'rotate-180'
              )}
            />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
          {menu}
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
};
